import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const SeoAnalyzerViewResult = ({result}) => {
  const getQualityColor = (quality) => {
    switch(quality?.toLowerCase()) {
      case 'excellent':
        return 'bg-success/20 text-success';
      case 'good':
        return 'bg-info/20 text-info';
      case 'fair':
        return 'bg-warning/20 text-warning';
      case 'poor':
        return 'bg-error/20 text-error';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  const getOptimizationColor = (level) => {
    switch(level?.toLowerCase()) {
      case 'high':
        return 'bg-success/20 text-success';
      case 'medium':
        return 'bg-warning/20 text-warning';
      case 'low':
        return 'bg-error/20 text-error';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* SEO Status Section */}
        <div className="space-y-4">
          {/* Technical Score Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.ChartBar size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Technical SEO Score</h3>
              </div>

              <div className="space-y-3">
                {/* Score Gauge */}
                <div className="p-4 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Overall Score</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      result?.analysis?.seo_status?.technical_score > 0.7 ? 'bg-success/20 text-success' :
                      result?.analysis?.seo_status?.technical_score > 0.4 ? 'bg-warning/20 text-warning' :
                      'bg-error/20 text-error'
                    }`}>
                      {Math.round(result?.analysis?.seo_status?.technical_score * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        result?.analysis?.seo_status?.technical_score > 0.7 ? 'bg-success' :
                        result?.analysis?.seo_status?.technical_score > 0.4 ? 'bg-warning' :
                        'bg-error'
                      }`}
                      style={{width: `${result?.analysis?.seo_status?.technical_score * 100}%`}}
                    ></div>
                  </div>
                </div>

                {/* Content Metrics */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="text-sm text-base-content/70">Content Quality</span>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getQualityColor(result?.analysis?.seo_status?.content_quality)}`}>
                        {result?.analysis?.seo_status?.content_quality}
                      </span>
                    </div>
                  </div>

                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="text-sm text-base-content/70">Keyword Optimization</span>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getOptimizationColor(result?.analysis?.seo_status?.keyword_optimization)}`}>
                        {result?.analysis?.seo_status?.keyword_optimization}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recommendations Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-info/10">
                  <PhosphorIcons.ListChecks size={20} weight="bold" className="text-info"/>
                </div>
                <h3 className="card-title text-lg">Recommendations</h3>
              </div>

              <div className="space-y-2">
                {result?.analysis?.seo_status?.recommendations?.map((recommendation, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-base-200 rounded-lg">
                    <PhosphorIcons.CaretRight size={16} className="text-primary"/>
                    <span className="text-sm capitalize">{recommendation}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Content Validation Section */}
        <div className="space-y-4">
          {/* Content Details Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.Article size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Content Details</h3>
              </div>

              <div className="space-y-3">
                {/* Validation Status */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Status</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      result?.analysis?.validation?.is_valid 
                        ? 'bg-success/20 text-success' 
                        : 'bg-error/20 text-error'
                    }`}>
                      {result?.analysis?.validation?.is_valid ? 'Valid' : 'Invalid'}
                    </span>
                  </div>
                </div>

                {/* Content Metrics Grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="text-sm text-base-content/70">Content Type</span>
                    <div className="font-medium capitalize mt-1">
                      {result?.analysis?.validation?.content_type?.replace(/_/g, ' ')}
                    </div>
                  </div>

                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="text-sm text-base-content/70">Word Count</span>
                    <div className="font-medium mt-1">
                      {result?.analysis?.validation?.word_count} words
                    </div>
                  </div>

                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="text-sm text-base-content/70">Search Intent</span>
                    <div className="font-medium capitalize mt-1">
                      {result?.analysis?.seo_status?.search_intent}
                    </div>
                  </div>

                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="text-sm text-base-content/70">Confidence</span>
                    <div className="font-medium mt-1">
                      {Math.round(result?.analysis?.validation?.confidence * 100)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeoAnalyzerViewResult;